import {useMemo} from 'react';
import {useUserTokenOrSessionId} from '../userStore/user.selector';
import {useBasketMinimalState} from './useBasketMinimalState';
// eslint-disable-next-line monorepo/no-internal-import
import {useCampaignKey} from '@aboutyou/logic-components/src/backend-services/Campaigns/campaign-state/campaign.hooks';
import {reduceVariantIdsToProductIds} from '../../utils/reduceVariantIdsToProductIds';

export const useBasketCount = () => {
  const {data} = useBasketMinimalState();

  return data?.state.totalQuantity ?? 0;
};

export const useIsProductInBasket = (productID?: number): boolean => {
  const {data} = useBasketMinimalState();
  return !!(
    productID && data?.state.items.some(item => item.productId === productID)
  );
};

export const useBasketItemForProduct = (productID?: number) => {
  const {data} = useBasketMinimalState();
  return data?.state.items.find(item => item.productId === productID);
};

export const useIsSizeInBasket = () => {
  const {data} = useBasketMinimalState();

  return (sizeId?: number) =>
    !!(sizeId && data?.state.items.some(item => item.sizeId === sizeId));
};
export const useQuantityInBasketBySizeId = (sizeId?: number): number => {
  const {data} = useBasketMinimalState();
  return data?.state.items.find(item => item.sizeId === sizeId)?.quantity ?? 0;
};

export const useBasketSession = () => {
  const {data: session, isLoading} = useUserTokenOrSessionId();
  const campaignKey = useCampaignKey();

  const cacheKey = useMemo(() => {
    return `fetch-basket-${session?.resolvedId || ''}${campaignKey || ''}`;
  }, [campaignKey, session?.resolvedId]);

  return {
    session,
    cacheKey,
    isLoading,
    campaignKey,
  };
};

export const useBasketContainsCampaignOrCouponItems = () => {
  const {data: basket} = useBasketMinimalState();

  return (
    basket?.activeReduction?.$case === 'campaignReduction' ||
    basket?.activeReduction?.$case === 'couponReduction'
  );
};

export const useIsBasketStateLoaded = () => {
  const {data, isLoading} = useBasketMinimalState();

  return data && !isLoading;
};

export const useBasketStateData = () => {
  const {data} = useBasketMinimalState();

  return data;
};

export const useBasketStateItems = () => {
  const data = useBasketStateData();
  return data?.state?.items || [];
};

export const useBasketStateTotalQuantity = () => {
  const data = useBasketStateData();

  return data?.state.totalQuantity || 0;
};

export const useHasBasketStateData = () => {
  const totalQuantity = useBasketStateTotalQuantity();

  return totalQuantity > 0;
};

export const useBasketStateBasketKey = () => {
  const data = useBasketStateData();

  return data?.state.basketKey;
};

export const useBasketStateTotalPrice = () => {
  const data = useBasketStateData();

  return data?.state.price;
};

export const useIsBasketStateLoading = () => {
  const {isLoading} = useBasketMinimalState();
  return isLoading;
};

export const useBasketProductIdsWithSizeIds = () => {
  const basketStateItems = useBasketStateItems();
  return reduceVariantIdsToProductIds(basketStateItems);
};
